import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { fetchSessions, playNotificationSound } from './api';
import { displayName } from './types';

const POLL_MS = 8000;

/** Ask for desktop notifications; call from a click so browsers allow the prompt. */
export const requestDesktopNotifications = () => {
  try {
    if ('Notification' in window && Notification.permission === 'default') Notification.requestPermission();
  } catch {
    // unsupported browser
  }
};

/**
 * While the admin is marked online, watch for visitors asking for a person
 * from any admin page: chime, toast with a link to the chat, and a desktop
 * notification when the tab is in the background. The Live chat page raises
 * its own alerts, so this stays quiet there. Returns the waiting count for
 * the nav badge.
 */
export const useWaitingChatAlerts = (enabled: boolean, onLiveChatPage: boolean) => {
  const navigate = useNavigate();
  const [waiting, setWaiting] = useState(0);
  const seen = useRef<Set<string> | null>(null);
  const quiet = useRef(onLiveChatPage);
  quiet.current = onLiveChatPage;

  useEffect(() => {
    if (!enabled) {
      setWaiting(0);
      seen.current = null;
      return;
    }
    let cancelled = false;

    const check = async () => {
      try {
        const { sessions } = await fetchSessions();
        if (cancelled) return;
        const waitingNow = sessions.filter((s) => s.status === 'waiting_for_admin');
        setWaiting(waitingNow.length);

        // First poll only records what is already waiting.
        if (seen.current === null) {
          seen.current = new Set(waitingNow.map((s) => s.id));
          return;
        }
        const fresh = waitingNow.filter((s) => !seen.current!.has(s.id));
        seen.current = new Set(waitingNow.map((s) => s.id));
        if (quiet.current) return;

        fresh.forEach((s) => {
          const name = displayName(s);
          const open = () => navigate(`/admin/livechat?s=${s.id}`);
          playNotificationSound();
          toast.warning(`${name} wants to talk to someone`, {
            description: s.last_message || s.current_page || undefined,
            duration: 30000,
            action: { label: 'Open chat', onClick: open },
          });
          try {
            if ('Notification' in window && Notification.permission === 'granted' && document.hidden) {
              const n = new Notification('Live chat: someone is waiting', { body: `${name}: ${s.last_message || 'asked for a team member'}`, tag: s.id });
              n.onclick = () => {
                window.focus();
                open();
                n.close();
              };
            }
          } catch {
            // notifications unavailable
          }
        });
      } catch {
        // offline or signed out; the next poll tries again
      }
    };

    check();
    const id = setInterval(check, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [enabled, navigate]);

  // Show the queue in the tab title so it is visible from other tabs.
  useEffect(() => {
    const base = document.title.replace(/^\(\d+\)\s*/, '');
    document.title = waiting > 0 ? `(${waiting}) ${base}` : base;
  }, [waiting]);

  return waiting;
};
